import JSDB from '../../fromKotlin/nk';
import {ORDER_SHOP_HOST} from '../../utils/whatsappShareUrl';

// Flask API base (same host the AI keys api uses).
const API_BASE = (process.env.REACT_APP_API_BASE || 'https://nihulkav.online:4000').replace(/\/$/, '');

// Public v2 app base — clientsign, driver, collage all live under /v2.
export const V2_APP_BASE = `${ORDER_SHOP_HOST}/v2`;
/** @deprecated use V2_APP_BASE */
export const ORDER_APP_BASE = V2_APP_BASE;

// Permission bitmask — mirrors internal_server_config.py.
export const PERM = {
    SEE: 1,
    EDIT: 2,
    DONE: 4,
    PRICES: 8,
    SUMS: 16,
    ROUTE: 32,
    BOXES: 64,
    FEEDBACK: 128,
} as const;

function getHeaders(): HeadersInit {
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${JSDB().getToken()}`,
    };
}

function companyCtx(): { company_id: string; company: string } {
    const user = JSDB().getUser().first;
    return {
        company_id: String(user?.company_id ?? ''),
        company: JSDB().getCompany(),
    };
}

async function parse(res: Response): Promise<any> {
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.status !== 'SUCCESS') {
        throw new Error(json?.status_msg || 'request failed');
    }
    const raw = json.status_msg ?? json.data;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw);
        } catch {
            // Backend Success() without dump=True stringifies Python lists as "['a']".
            try {
                const normalized = raw.replace(/'/g, '"');
                return JSON.parse(normalized);
            } catch {
                return raw;
            }
        }
    }
    return raw;
}

/** Mint a single-use, 1-hour client self-signup link (new client). */
export async function createSignupLink(): Promise<string> {
    const ctx = companyCtx();
    const res = await fetch(`${API_BASE}/clientsign/register`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(ctx),
    });
    const pass = await parse(res);
    return `${V2_APP_BASE}/clientsign?pass=${encodeURIComponent(pass)}`;
}

/**
 * Mint a single-use, 1-hour link for an existing client to update card fields
 * and upload signed documents (saves under client_data on the client card).
 */
export async function createClientEditLink(clientId: string | number): Promise<string> {
    const ctx = companyCtx();
    const res = await fetch(`${API_BASE}/clientsign/register`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...ctx, client_id: String(clientId) }),
    });
    const pass = await parse(res);
    return `${V2_APP_BASE}/clientsign?pass=${encodeURIComponent(pass)}`;
}

export interface ClientSignDocDef {
    key: string;
    label: string;
    required?: number;
}

/** Requested client_data docs from cofc_conf.clientsign_docs. */
export function getConfiguredClientSignDocs(): ClientSignDocDef[] {
    const raw = (JSDB().userCofcConf as { clientsign_docs?: string | ClientSignDocDef[] | null } | null)
        ?.clientsign_docs;
    if (!raw || raw === 'None' || raw === 'NULL') return [];
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((d: any) => ({
                key: String(d?.key ?? '').trim(),
                label: String(d?.label ?? d?.key ?? '').trim(),
                required: Number(d?.required ?? 0),
            }))
            .filter((d) => d.key);
    } catch {
        return [];
    }
}

export interface DriverLinkOptions {
    mode: 'driver' | 'orders';
    perm: number; // bitmask of PERM.*
    driverId?: string | number; // mode 'driver'
    orderIds?: (string | number)[]; // mode 'orders'
    routeStops?: DriverRouteStop[]; // optional structured route, mode 'orders'
}

export interface DriverRouteStop {
    order_id: string | number;
    date: string;
    position: number;
    boxes: number;
    boxes_source: string;
    boxes_override: boolean;
    editable_fields: string[];
    field_labels: Record<string, string>;
}

/** Mint an external-driver link with scope + permission claims. */
export async function createDriverLink(opts: DriverLinkOptions): Promise<string> {
    const ctx = companyCtx();
    const body: Record<string, any> = { ...ctx, mode: opts.mode, perm: opts.perm };
    if (opts.mode === 'driver') {
        body.driver_id = String(opts.driverId ?? '-1');
    } else {
        // Comma-separated string: acceptArgs used to crash on string[] (assumed list of dicts).
        // Server register2 uses acceptArgs2 and already parses both list and CSV forms.
        body.order_ids = (opts.orderIds ?? []).map(String).join(',');
        if (opts.routeStops) {
            // Nested ints/bools/arrays are fine with acceptArgs2 on /external/register2.
            body.route_stops = opts.routeStops.map((stop) => ({
                ...stop,
                order_id: String(stop.order_id),
            }));
        }
    }
    const res = await fetch(`${API_BASE}/external/register2`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
    });
    const pass = await parse(res);
    return `${V2_APP_BASE}/driver?pass=${encodeURIComponent(pass)}`;
}

/** Permanent attendance punch link for one company user. Inactive users are rejected. */
export async function createAttendanceLink(userId: string | number): Promise<string> {
    const ctx = companyCtx();
    const res = await fetch(`${API_BASE}/external/attendance/register`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...ctx, user_id: String(userId) }),
    });
    const pass = await parse(res);
    if (typeof pass !== 'string' || !pass.trim()) {
        throw new Error('יצירת הקישור נכשלה');
    }
    return `${V2_APP_BASE}/attendance?pass=${encodeURIComponent(pass.trim())}`;
}

/** List the document names a client uploaded through the self-signup link. */
export async function listClientDataDocs(clientId: string | number): Promise<string[]> {
    const ctx = companyCtx();
    const res = await fetch(`${API_BASE}/clients/client_data/list`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...ctx, client_id: String(clientId) }),
    });
    const list = await parse(res);
    return Array.isArray(list) ? list.map(String) : [];
}

/** Staff upload / replace a client_data document image. */
export async function uploadClientDataDoc(
    clientId: string | number,
    docName: string,
    file: File,
): Promise<void> {
    const ctx = companyCtx();
    const form = new FormData();
    form.append('company_id', ctx.company_id);
    form.append('company', ctx.company);
    form.append('client_id', String(clientId));
    form.append('doc_name', docName);
    form.append('image', file);
    const res = await fetch(`${API_BASE}/clients/client_data/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${JSDB().getToken()}` },
        body: form,
    });
    await parse(res);
}

/** Direct (jwt-in-query) URL to view/download a single client document image. */
export function clientDataDocUrl(
    clientId: string | number,
    docName: string,
    cacheBust?: number | string,
): string {
    const ctx = companyCtx();
    const params = new URLSearchParams({
        company: ctx.company,
        company_id: ctx.company_id,
        client_id: String(clientId),
        doc_name: docName,
        jwt: JSDB().getToken(),
    });
    if (cacheBust != null) params.set('t', String(cacheBust));
    return `${API_BASE}/clients/client_data/get?${params.toString()}`;
}
